import { useEffect } from 'react';
import { useAuth } from 'auth/AuthProvider';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import {
  SEGMENT_STORAGE_EVENT,
  SEGMENT_STORAGE_KEYS,
  SegmentStorageEventDetail,
  SegmentStorageKey,
} from 'utils/segmentStorageBridge';

const KEY_TO_ATTRIBUTE: Record<SegmentStorageKey, string> = {
  currentBrand: 'data-segment-brand',
  currentFaixa: 'data-segment-tier',
  currentTier: 'data-segment-tier',
  currentGeneric: 'data-segment-generic',
};

const POLL_INTERVAL_MS = 1000;
const POLL_MAX_TICKS = 30;

const applyFromStorage = (key: SegmentStorageKey): void => {
  const attr = KEY_TO_ATTRIBUTE[key];
  const value = ApiLocalStorage.get(key);
  const externalCode = value && typeof value.externalCode === 'string' ? value.externalCode : '';
  const current = document.body.getAttribute(attr);

  if (externalCode) {
    if (current !== externalCode) document.body.setAttribute(attr, externalCode);
  } else if (current !== null) {
    document.body.removeAttribute(attr);
  }
};

const clearAllAttributes = (): void => {
  SEGMENT_STORAGE_KEYS.forEach((key) => {
    const attr = KEY_TO_ATTRIBUTE[key];
    if (document.body.getAttribute(attr) !== null) {
      document.body.removeAttribute(attr);
    }
  });
};

export const useSegmentBodyAttributes = (): void => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      clearAllAttributes();
      return;
    }

    SEGMENT_STORAGE_KEYS.forEach(applyFromStorage);

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<SegmentStorageEventDetail>).detail;
      if (detail?.key) {
        applyFromStorage(detail.key);
      }
    };

    window.addEventListener(SEGMENT_STORAGE_EVENT, handler);

    let ticks = 0;
    const interval = window.setInterval(() => {
      ticks += 1;
      SEGMENT_STORAGE_KEYS.forEach(applyFromStorage);
      if (ticks >= POLL_MAX_TICKS) window.clearInterval(interval);
    }, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener(SEGMENT_STORAGE_EVENT, handler);
      window.clearInterval(interval);
    };
  }, [user]);
};
